import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaThLarge, FaList } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import '../styles/News.css';

const API_BASE = process.env.REACT_APP_API_BASE || "";

export default function News() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [viewMode, setViewMode] = useState('gallery');
  const navigate = useNavigate();

  const getLangData = (item, field) => {
    const lang = (i18n.language || 'KR').toLowerCase();
    return item[`${field}_${lang}`] || item[`${field}_kr`] || "";
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    fetch(`${API_BASE}/api/news_list.php`)
      .then(res => res.json())
      .then(data => setItems(Array.isArray(data) ? data : []));
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const itemsPerPage = useMemo(() => {
    if (viewMode === 'list') return 10;
    return windowWidth <= 768 ? 4 : 6;
  }, [windowWidth, viewMode]);

  const { currentItems, totalPages } = useMemo(() => {
    const lastIdx = currentPage * itemsPerPage;
    return {
      currentItems: items.slice(lastIdx - itemsPerPage, lastIdx),
      totalPages: Math.ceil(items.length / itemsPerPage)
    };
  }, [items, currentPage, itemsPerPage]);

  return (
    <>
      <Helmet>
        <title>배우 한지우 뉴스</title>
        <meta name="description" content="배우 한지우의 최신 소식을 확인하세요." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="배우 한지우 뉴스" />
        <meta property="og:description" content="배우 한지우의 최신 소식을 확인하세요." />
        <meta property="og:url" content="https://hanjiwoo.com/news" />
        <meta property="og:image" content="https://hanjiwoo.com/og/og-home.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="배우 한지우 뉴스" />
        <meta name="twitter:description" content="배우 한지우의 최신 소식을 확인하세요." />
        <meta name="twitter:image" content="https://hanjiwoo.com/og/og-home.jpg" />

        <link rel="canonical" href="https://hanjiwoo.com/news" />
      </Helmet>
      <div className="sub-page-container news-page">
        <div className="news-header">
          <h1 className="section-title">{t('nav_news')}</h1>
        </div>

        <div className="view-controls-wrapper">
          <div className="view-controls">
            <button 
              className={`switch-btn ${viewMode === 'gallery' ? 'active' : ''}`} 
              onClick={() => { setViewMode('gallery'); setCurrentPage(1); }}
            >
              <FaThLarge size={18} />
            </button>
            <button 
              className={`switch-btn ${viewMode === 'list' ? 'active' : ''}`} 
              onClick={() => { setViewMode('list'); setCurrentPage(1); }}
            >
              <FaList size={18} />
            </button>
            </div>
          </div>

          {/* 4. viewMode 클래스 동적 부여 */}
          <div className={`news-container ${viewMode}-view`}>
            {items.length > 0 ? (
              currentItems.map((item, idx) => (
                // <div key={item.id} className="news-card" onClick={() => setSelected(item)} style={{"--delay": idx}}>
                <div key={item.id} className="news-card" onClick={() => navigate(`/news/${item.id}`)} style={{"--delay": idx}} data-title={getLangData(item, 'title')}>
                  {viewMode === 'gallery' && (
                    <div className="news-card-thumb">
                      {item.thumbnail ? (
                        <img src={`${API_BASE}${item.thumbnail}`} alt={getLangData(item, 'title')} />
                      ) : (
                        <img src="/logo192.png" alt="Han Ji Woo News" className="default-thumb"/>
                      )}
                    </div>
                  )}
                  <div className="news-card-info">
                    <span className="news-date">{item.date?.replace(/-/g, '.')}</span>
                    <h3 className="news-title">{getLangData(item, 'title')}</h3>
                    {/* {viewMode === 'list' && <span className="list-arrow">→</span>} */}
                  </div>
                </div>
              ))
                ) : (
                /* 데이터가 없을 때 표시할 문구 */
                <div className="no-data">
                  <p>{t('no_data_news')}</p>
                </div>
              )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>&lt;</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} className={currentPage === i + 1 ? 'active' : ''} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
              ))}
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>&gt;</button>
            </div>
          )}

          {selected && (
            <div className="news-modal-overlay" onClick={() => setSelected(null)}>
              <div className="news-modal-content ql-snow" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <span className="modal-date">{selected.date}</span>
                  <h2 className="modal-title">{getLangData(selected, 'title')}</h2>
                  <button className="modal-close-btn" onClick={() => setSelected(null)}>&times;</button>
                </div>
                <div className="modal-body ql-editor" dangerouslySetInnerHTML={{ __html: getLangData(selected, 'content') }} />
              </div>
            </div>
          )}
      </div>
    </>
  );
}